import React, { useState, useEffect } from 'react';
import { Container, Navbar, Nav, Tab, Tabs } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { VERSION, BUILD_DATE } from './version';
import Login from './components/Login';
import ThemeToggle from './components/ThemeToggle';

import BASForm from './components/BASForm';
import BASList from './components/BASList';
import PDFUpload from './components/PDFUpload';
import ATOValidation from './components/ATOValidation';
import ABNValidation from './components/ABNValidation';
import ATOSubmission from './components/ATOSubmission';
import ATOModeToggle from './components/ATOModeToggle';
import ATOAuthSuccess from './components/ATOAuthSuccess';
import ATOAuthError from './components/ATOAuthError';
import UserProfile from './components/UserProfile';
import STPPayroll from './components/STPPayroll';
import LogsViewer from './components/LogsViewer';
import AdminPanel from './components/AdminPanel';

function AppContent() {
  const { user, logout, isAuthenticated } = useAuth();
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [activeTab, setActiveTab] = useState('form');
  const [basForms, setBasForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showATOSubmission, setShowATOSubmission] = useState(false);
  const [formForSubmission, setFormForSubmission] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBASForms();
    }
  }, [isAuthenticated]);

  // Handle OAuth callback pages
  const urlParams = new URLSearchParams(window.location.search);
  const isATOAuthSuccess = window.location.pathname.includes('ato-auth-success');
  const isATOAuthError = window.location.pathname.includes('ato-auth-error');

  if (isATOAuthSuccess) {
    return <ATOAuthSuccess />;
  }

  if (isATOAuthError) {
    return <ATOAuthError />;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const fetchBASForms = async () => {
    try {
      const response = await fetch('/api/bas-forms');
      const data = await response.json();
      setBasForms(data);
    } catch (error) {
      console.error('Error fetching BAS forms:', error);
    }
  };

  const handleFormSubmit = (formData) => {
    fetchBASForms(); // Refresh the list
    setActiveTab('list');
  };

  const handleFormSelect = (form) => {
    setSelectedForm(form);
    setActiveTab('form');
  };

  const handleATOSubmission = (form) => {
    setFormForSubmission(form);
    setShowATOSubmission(true);
  };

  const handleCloseATOSubmission = () => {
    setShowATOSubmission(false);
    setFormForSubmission(null);
  };

  return (
    <div className="App" style={{ background: colors.background, minHeight: '100vh' }}>
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-calculator me-3" style={{ fontSize: '2.5rem', color: colors.primary }}></i>
            <h2 className="mb-0" style={{ color: colors.text }}>
              123 Accounting - BAS Management
            </h2>
          </div>
          <div className="d-flex align-items-center">
            <ThemeToggle />
            <button
              onClick={logout}
              className="btn btn-outline-secondary ms-3"
              style={{ color: colors.text, borderColor: colors.border }}
            >
              <i className="fas fa-sign-out-alt me-1"></i>
              Logout ({user?.email})
            </button>
          </div>
        </div>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="form" title="BAS Form Entry">
            <BASForm 
              formData={selectedForm} 
              onSubmit={handleFormSubmit}
              onCancel={() => setSelectedForm(null)}
            />
          </Tab>
          
          <Tab eventKey="list" title="BAS Forms List">
            <BASList 
              forms={basForms} 
              onEdit={handleFormSelect}
              onDelete={fetchBASForms}
              onATOSubmission={handleATOSubmission}
            />
          </Tab>
          
          <Tab eventKey="upload" title="PDF Upload">
            <PDFUpload onUpload={fetchBASForms} />
          </Tab>
          
          <Tab eventKey="validation" title="ATO Validation">
            <ATOValidation />
          </Tab>
          
          <Tab eventKey="abn" title="ABN Validation">
            <ABNValidation />
          </Tab>
          
          <Tab eventKey="ato-mode" title="ATO Integration">
            <ATOModeToggle />
          </Tab>
          
          <Tab eventKey="stp" title="STP Payroll">
            <STPPayroll />
          </Tab>
          
          <Tab eventKey="logs" title="System Logs">
            <LogsViewer />
          </Tab>
          
          <Tab eventKey="profile" title="User Profile">
            <UserProfile />
          </Tab>
          
          {user?.role === 'admin' && (
            <Tab eventKey="admin" title="Admin Panel">
              <AdminPanel />
            </Tab>
          )}
        </Tabs>
      </Container>

      {/* ATO Submission Modal */}
      {showATOSubmission && formForSubmission && (
        <ATOSubmission 
          form={formForSubmission}
          onClose={handleCloseATOSubmission}
        />
      )}

      {/* Footer */}
      <footer 
        className="border-top mt-5 py-3"
        style={{ 
          background: colors.surface, 
          borderColor: colors.border,
          color: colors.textSecondary
        }}
      >
        <Container>
          <div className="text-center">
            <small>
              Copyright © 2025 123Group Pty Ltd | Version {VERSION} - Build {BUILD_DATE}
            </small>
          </div>
        </Container>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
